// @ts-strict-ignore
import { Auth } from 'aws-amplify';
import { Formik } from 'formik';
import * as React from 'react';
import { useHistory } from 'react-router-dom';
import * as yup from 'yup';

import { trackEvent } from '@/analytics/trackEvent';
import { ErrorText } from '@/atoms/ErrorText';
import { RouterLink } from '@/atoms/RouterLink/RouterLink';
import { useUserPublicId } from '@/components/AppHeader/hooks/useUserPublicId';
import {
  AuthForm,
  FormFooter,
  InputsWrapper,
} from '@/components/AuthV2/wrappers';
import { Button } from '@/components/Button/Button';
import { FormikInputField } from '@/components/Input/Input';
import { PasswordField } from '@/components/PasswordField';
import { useResponsiveConstants } from '@/hooks/responsive/useResponsiveConstants';
import { useQueryParams } from '@/hooks/useQueryParams';
import { AUTH_ROUTES } from '@/nav-routes';
import { postLoginMessage } from '@/utils/broadcastMessages';

const validationSchema = yup.object({
  email: yup
    .string()
    .strict(true)
    .trim('Email contains white spaces, please remove them')
    .email('Please provide a valid email')
    .required('Please provide a valid email'),
  password: yup
    .string()
    .strict(true)
    .min(8, 'Password must be 8 characters long')
    .required('Please provide a valid password'),
});

export const LoginForm = function (): JSX.Element {
  const history = useHistory();
  const [_, __, queryParams] = useQueryParams();

  const { mobile } = useResponsiveConstants();

  const [error, setError] = React.useState<string | null>(() => {
    const error =
      window.localStorage.getItem('error_description') ||
      queryParams.error_description;
    if (error) {
      if (error.match(/native user signup/gi)) {
        trackEvent('account_not_found');
        window.localStorage.removeItem('error_description');
        return 'Account not found';
      } else if (error.match(/signups are not allowed/gi)) {
        return 'Access to Needl.ai is restricted. If you need assistance, please contact support@needl.ai.';
      }
      return null;
    }
    return null;
  });

  const { refetch: trackUser } = useUserPublicId({
    enabled: false,
    onSuccess: () => {
      trackEvent('signin', { method: 'email' });
      history.replace('/');
      window.location.reload();
    },
  });

  return (
    <Formik
      validateOnChange
      initialValues={{ email: '', password: '' }}
      onSubmit={async (data, { setSubmitting }) => {
        setSubmitting(true);
        setError(null);
        try {
          await Auth.signIn(data.email.toLowerCase(), data.password.trim());
          await trackUser();
          setSubmitting(false);

          postLoginMessage();
        } catch (e) {
          if (e && e.code === 'UserNotConfirmedException') {
            setError('Unverified user.');
          } else if (
            e &&
            e.code === 'NotAuthorizedException' &&
            e.message.match(/User is disabled/gi)
          ) {
            setError('Something went wrong. Contact support@needl.ai');
          } else if (e && e.code === 'NotAuthorizedException') {
            setError('Incorrect username or password.');
          } else setError('Unknown error.');
          setSubmitting(false);
        }
      }}
      validationSchema={validationSchema}
    >
      {({ isSubmitting, isValid }) => (
        <AuthForm>
          <InputsWrapper>
            <FormikInputField
              placeholder='Email'
              name='email'
              type='text'
              label='Email'
              labelHidden
              autoComplete='on'
              id='email'
            />
            <PasswordField
              placeholder='Password'
              name='password'
              label='Password'
              labelHidden
              autoComplete='on'
              hidePasswordHint
              id='password'
            />
          </InputsWrapper>
          <FormFooter>
            {error ? <ErrorText>{error}</ErrorText> : null}
            <RouterLink
              to={AUTH_ROUTES.FORGOT_PASSWORD}
              color='emerald'
              dark
              id='forgot-password'
            >
              Forgot Password?
            </RouterLink>
            <Button
              loading={isSubmitting}
              disabled={!isValid || isSubmitting}
              fullWidth
              label='Login'
              size={mobile ? 'medium' : 'large'}
              variant='primary'
              submitButton
              id='login-button'
            />
          </FormFooter>
        </AuthForm>
      )}
    </Formik>
  );
};
